import { Box, Text } from '@chakra-ui/react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

export interface MentionItem {
  id: string;
  label: string;
}

export interface MentionSuggestionHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

interface MentionSuggestionProps {
  items: MentionItem[];
  command: (item: MentionItem) => void;
}

export const MentionSuggestion = forwardRef<
  MentionSuggestionHandle,
  MentionSuggestionProps
>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) {
        command(item);
      }
    };

    const upHandler = () => {
      if (items.length === 0) return;
      setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
    };

    const downHandler = () => {
      if (items.length === 0) return;
      setSelectedIndex((prev) => (prev + 1) % items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();

          return true;
        }
        if (event.key === 'ArrowDown') {
          downHandler();

          return true;
        }
        if (event.key === 'Enter') {
          enterHandler();

          return true;
        }

        return false;
      },
    }));

    if (items.length === 0) {
      return null;
    }

    return (
      <Box
        bg="gray.800"
        border="1px solid"
        borderColor="whiteAlpha.200"
        borderRadius="md"
        maxH="200px"
        overflowY="auto"
        py={1}
      >
        {items.map((item, index) => (
          <Box
            key={item.id}
            bg={index === selectedIndex ? 'whiteAlpha.200' : 'transparent'}
            cursor="pointer"
            px={3}
            py={2}
            onClick={() => selectItem(index)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <Text fontSize="sm">{item.label}</Text>
          </Box>
        ))}
      </Box>
    );
  }
);

MentionSuggestion.displayName = 'MentionSuggestion';
